import { useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SwatchGrid } from './WorkbenchColorPicker'
import type { FibLevel } from './types'

export interface FibConfigPatch {
  levels?: FibLevel[]
  labelMode?: 'values' | 'percent'
  fillColor?: string
  lineSize?: number
  extend?: string
}

interface FibLevelsPopoverProps {
  levels: FibLevel[]
  labelMode?: 'values' | 'percent'
  onChange: (patch: FibConfigPatch) => void
}

/**
 * The fib "levels" settings (the only part of TV's big fib box we build). A row
 * per level: enable toggle + ratio + per-level colour. Edits merge into the
 * overlay's extendData.
 */
export function FibLevelsPopover({ levels, labelMode = 'values', onChange }: FibLevelsPopoverProps) {
  const [editing, setEditing] = useState<number | null>(null) // index whose colour grid is open

  const toggle = (i: number) =>
    onChange({ levels: levels.map((l, idx) => (idx === i ? { ...l, enabled: l.enabled === false } : l)) })
  const setColor = (i: number, c: string) => {
    onChange({ levels: levels.map((l, idx) => (idx === i ? { ...l, color: c } : l)) })
    setEditing(null)
  }
  const fmt = (v: number) => (labelMode === 'percent' ? `${Number((v * 100).toFixed(1))}%` : `${v}`)

  return (
    <div className="flex w-44 flex-col gap-1.5">
      {/* Values ↔ Percent (checkbox) — applies to every level label */}
      <button
        type="button"
        onClick={() => onChange({ labelMode: labelMode === 'percent' ? 'values' : 'percent' })}
        className="flex items-center gap-2 rounded-md px-1.5 py-1 text-caption text-foreground hover:bg-muted/60 transition-colors cursor-pointer"
      >
        <span
          className={cn(
            'inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border',
            labelMode === 'percent' ? 'bg-muted border-border text-foreground' : 'border-border text-transparent',
          )}
        >
          {labelMode === 'percent' && <Check size={11} strokeWidth={3} />}
        </span>
        Show as percent
      </button>
      <div className="h-px bg-border" />

      <div className="flex max-h-64 flex-col gap-0.5 overflow-y-auto pr-0.5">
        {levels.map((l, i) => {
          const on = l.enabled !== false
          return (
            <div key={l.value} className="relative flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-muted/60">
              <button
                type="button"
                onClick={() => toggle(i)}
                aria-label={`Toggle ${l.value}`}
                className={cn(
                  'inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border cursor-pointer',
                  on ? 'bg-muted border-border text-foreground' : 'border-border text-transparent',
                )}
              >
                {on && <Check size={11} strokeWidth={3} />}
              </button>
              <span className="flex-1 text-caption tabular-nums text-foreground">{fmt(l.value)}</span>
              <button
                type="button"
                onClick={() => setEditing(editing === i ? null : i)}
                aria-label={`Colour ${l.value}`}
                className="h-4 w-4 rounded-sm border border-border/50 cursor-pointer"
                style={{ backgroundColor: l.color }}
              />
              {editing === i && (
                <div className="absolute right-0 top-full z-20 mt-1 rounded-md border border-border bg-popover p-1.5 shadow-popover">
                  <SwatchGrid cur={l.color} onPick={(c) => setColor(i, c)} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
